/**
 * Organization service — CRUD for user organizations.
 */

import axiosInstance from './axiosInstance';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const toApiAssetUrl = (url?: string | null): string | null => {
  if (!url) return null;
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  if (url.startsWith('/')) return `${API_URL}${url}`;

  return url;
};

const organizationMemberAvatarUrl = (member: OrganizationMember): string | null => {
  if (member.avatar_url?.endsWith('/api/v1/profile/image/serve') || member.avatar_url === '/api/v1/profile/image/serve') {
    return toApiAssetUrl(`/api/v1/profile/image/${member.user_id}`);
  }

  return toApiAssetUrl(member.avatar_url);
};

export interface Organization {
  id: string;
  name: string;
  website?: string | null;
  logo_url?: string | null;
  brand_colors?: { colors?: string[] } | null;
  role?: OrganizationRole;
  is_owner?: boolean;
  member_count?: number;
  project_count?: number;
  is_unlimited?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export type OrganizationRole = 'owner' | 'admin' | 'manager' | 'member';

export interface OrganizationMember {
  id: string;
  user_id: string;
  email: string;
  name: string;
  avatar_url?: string | null;
  role: OrganizationRole;
  manager_user_id?: string | null;
  manager_name?: string | null;
  status?: 'accepted';
  is_current_user?: boolean;
  assigned_credits_balance?: number;
  created_at?: string | null;
}

export interface OrganizationInvitation {
  id: string;
  email: string;
  role: Exclude<OrganizationRole, 'owner'>;
  manager_user_id?: string | null;
  manager_name?: string | null;
  status: 'pending' | 'accepted' | 'declined' | 'expired' | 'cancelled';
  invited_by: string;
  invited_by_name: string;
  created_at?: string | null;
  expires_at?: string | null;
  accepted_at?: string | null;
  is_expired: boolean;
}

export interface OrganizationInvitationDetails {
  id: string;
  email: string;
  role: Exclude<OrganizationRole, 'owner'>;
  status: string;
  is_expired: boolean;
  organization_id: string;
  organization_name: string;
  inviter_name: string;
  expires_at?: string | null;
}

export interface OrganizationProject {
  id: string;
  title: string;
  description?: string | null;
  status: string;
  role: string;
  is_owner: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface OrganizationDetail {
  organization: Organization;
  members: OrganizationMember[];
  invitations: OrganizationInvitation[];
  projects: OrganizationProject[];
  available_projects: OrganizationProject[];
  can_manage_members: boolean;
  can_manage_projects: boolean;
}

class OrganizationService {
  async listOrganizations(): Promise<Organization[]> {
    const res = await axiosInstance.get('/api/v1/organizations/');

    return res.data;
  }

  async createOrganization(
    name: string,
    website: string | null,
    brandColors: string[] | null,
    logoFile: File | null,
  ): Promise<Organization> {
    const form = new FormData();

    form.append('name', name);
    if (website) form.append('website', website);
    if (brandColors) form.append('brand_colors', JSON.stringify({ colors: brandColors }));
    if (logoFile) form.append('logo', logoFile);

    const res = await axiosInstance.post('/api/v1/organizations/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return res.data;
  }

  async updateOrganization(
    orgId: string,
    name: string,
    website: string | null,
    brandColors: string[] | null,
    logoFile: File | null,
  ): Promise<Organization> {
    const form = new FormData();

    form.append('name', name);
    if (website) form.append('website', website);
    if (brandColors) form.append('brand_colors', JSON.stringify({ colors: brandColors }));
    if (logoFile) form.append('logo', logoFile);

    const res = await axiosInstance.put(`/api/v1/organizations/${orgId}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return res.data;
  }

  async getOrganization(orgId: string): Promise<OrganizationDetail> {
    const res = await axiosInstance.get(`/api/v1/organizations/${orgId}`);

    return {
      ...res.data,
      members: res.data.members.map((member: OrganizationMember) => ({
        ...member,
        avatar_url: organizationMemberAvatarUrl(member),
      })),
      invitations: res.data.invitations || [],
    };
  }

  async addMember(
    orgId: string,
    email: string,
    role: Exclude<OrganizationRole, 'owner'>,
    managerUserId?: string | null,
  ): Promise<OrganizationInvitation> {
    const res = await axiosInstance.post(`/api/v1/organizations/${orgId}/members`, {
      email,
      role,
      manager_user_id: managerUserId || null,
    });

    return res.data;
  }

  async getInvitation(token: string): Promise<OrganizationInvitationDetails> {
    const res = await axiosInstance.get(`/api/v1/organizations/invitations/${token}`);

    return res.data;
  }

  async acceptInvitation(token: string): Promise<{ organization_id: string }> {
    const res = await axiosInstance.post(`/api/v1/organizations/invitations/${token}/accept`);

    return res.data;
  }

  async updateMember(
    orgId: string,
    userId: string,
    patch: {
      role?: Exclude<OrganizationRole, 'owner'>;
      manager_user_id?: string | null;
    },
  ): Promise<OrganizationMember> {
    const res = await axiosInstance.put(`/api/v1/organizations/${orgId}/members/${userId}`, patch);

    return res.data;
  }

  async updateMemberRole(
    orgId: string,
    userId: string,
    role: Exclude<OrganizationRole, 'owner'>,
  ): Promise<OrganizationMember> {
    return this.updateMember(orgId, userId, { role });
  }

  async removeMember(orgId: string, userId: string): Promise<void> {
    await axiosInstance.delete(`/api/v1/organizations/${orgId}/members/${userId}`);
  }

  async assignMemberCredits(orgId: string, userId: string, amount: number): Promise<OrganizationMember> {
    const res = await axiosInstance.post(`/api/v1/organizations/${orgId}/members/${userId}/credits`, { amount });

    return {
      ...res.data,
      avatar_url: organizationMemberAvatarUrl(res.data),
    };
  }

  async assignProject(orgId: string, projectId: string): Promise<OrganizationProject> {
    const res = await axiosInstance.post(`/api/v1/organizations/${orgId}/projects`, { project_id: projectId });

    return res.data;
  }

  async removeProject(orgId: string, projectId: string): Promise<void> {
    await axiosInstance.delete(`/api/v1/organizations/${orgId}/projects/${projectId}`);
  }

  async assignProjectMember(orgId: string, projectId: string, userId: string): Promise<{ assigned: boolean }> {
    const res = await axiosInstance.post(`/api/v1/organizations/${orgId}/projects/${projectId}/members`, { user_id: userId });
    return res.data;
  }

  async removeProjectMember(orgId: string, projectId: string, userId: string): Promise<void> {
    await axiosInstance.delete(`/api/v1/organizations/${orgId}/projects/${projectId}/members/${userId}`);
  }

  getLogoUrl(orgId: string): string {
    return `${API_URL}/api/v1/organizations/${orgId}/logo`;
  }
}

export const organizationService = new OrganizationService();
